
export interface IFormHelper {
    // you can pass in a selector or the actual elements
    form: string | HTMLFormElement;
    submitButton?: string | HTMLInputElement | HTMLButtonElement;
}

// valid elements
type FieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

interface IValidationElement {
    field: FieldElement;
    messageField?: Element;
    requiredMessage?: string;
    phoneMessage?: string;
    emailMessage?: string;
    creditcardMessage?: string; // this currently doesn't do anything!
    regexMessage?: string;
    maxlengthMessage?: string;
    minlengthMessage?: string;
}

type doFixup = (validElement: IValidationElement, attrval: string) => void;

const fixups: { [attrkey: string]: doFixup } = {
    "data-val-required": (validElement, attrval) => {
        validElement.requiredMessage = attrval;
        validElement.field.setAttribute('required', '');
    },
    "data-val-email": (validElement, attrval) => {
        validElement.emailMessage = attrval;
    },
    "data-val-phone": (validElement, attrval) => {
        validElement.phoneMessage = attrval;
    },
    "data-val-creditcard": (validElement, attrval) => {
        validElement.creditcardMessage = attrval;
    },
    "data-val-maxlength": (validElement, attrval) => {
        validElement.maxlengthMessage = attrval;
    },
    "data-val-maxlength-max": (validElement, attrval) => {
        validElement.field.setAttribute('maxlength', attrval);
    },
    "data-val-minlength": (validElement, attrval) => {
        validElement.minlengthMessage = attrval;
    },
    "data-val-minlength-min": (validElement, attrval) => {
        validElement.field.setAttribute('minlength', attrval);
    },
    "data-val-regex": (validElement, attrval) => {
        validElement.regexMessage = attrval;
    },
    "data-val-regex-pattern": (validElement, attrval) => {
        validElement.field.setAttribute('pattern', attrval);
    }
}

const DEFAULTINVALIDMESSAGE = "Field is not valid";

// Includes various functions to make forms more user friendly
// 100% javascript... no jquery
// related topics: 
// https://www.falldowngoboone.com/blog/native-form-validation-with-javascript/
// https://developer.mozilla.org/en-US/docs/Web/API/Constraint_validation
export class FormHelper {
    submitMessage: string;
    form: HTMLFormElement;
    submitButton: HTMLInputElement | HTMLButtonElement;
    isSubmitted: boolean;
    checkDoubleSubmit: boolean;
    checkValid: boolean;
    validationElements: Map<Element, IValidationElement>;
    submitConfirmWarning: string;

    constructor(setup: IFormHelper) {
        const self = this;
        if (typeof setup.form === 'string') {
            this.form = document.querySelector(setup.form);
        }
        else {
            this.form = setup.form;
        }
        if (this.form) {
            if (setup.submitButton) {
                if (typeof setup.submitButton === 'string') {
                    this.submitButton = this.form.querySelector(setup.submitButton);
                }
                else {
                    this.submitButton = setup.submitButton;
                }
            }
            else {
                this.submitButton = this.form.querySelector('input[type="submit"],button')
            }
        }
        else {
            if (setup.submitButton) {
                if (typeof setup.submitButton === 'string') {
                    this.submitButton = document.querySelector(setup.submitButton);
                }
                else {
                    this.submitButton = setup.submitButton;
                }
            }
        }

        this.submitMessage = "(processing)";
        this.checkDoubleSubmit = false;
        this.isSubmitted = false;
        this.checkValid = false;
        this.validationElements = new Map<Element, IValidationElement>;
        this.submitConfirmWarning = null;

        if (this.form) {
            this.form.addEventListener('submit', (e) => {
                self.submit(e);
            });
        }
        else {
            console.warn("FormHelper: form element not found.");
        }
    }

    // Prevent double-submission
    public preventDoubleSubmit() {
        this.checkDoubleSubmit = true;
    }

    // Add a confirmation popup to verify that you want to submit
    // the form?
    public addSubmitConfirmWarning(warning: string) {
        this.submitConfirmWarning = warning;
    }

    // Add the HTML required attribute to fields which have unobtrusive required validation
    // setup
    public addRequiredAttributes() {
        const allfields = this.form.elements;
        for (var idxEl = 0; idxEl < allfields.length; ++idxEl) {
            const field = allfields[idxEl];
            const valrequired = field.getAttribute("data-val-required");
            if (valrequired) {
                field.setAttribute('required', '');
            }
        }
    }

    // Check all elements for unobtrusive elements
    // and try to add our equivalents
    public addUnobtrusiveAttributes() {
        this.form.noValidate = true;
        this.checkValid = true;
        const allfields = this.form.elements;

        for (var idxEl = 0; idxEl < allfields.length; ++idxEl) {
            const field = allfields[idxEl];
            this.addUnobtrusiveField(<FieldElement>field);
        }
    }

    // Called when the form is submitted. 
    private submit(e: SubmitEvent) {
        // we're handling validity checks
        if (this.checkValid) {
            const isValid = this.form.reportValidity();
            if (!isValid) {
                e.preventDefault();
                return;
            }
        }

        if (this.submitConfirmWarning) {
            if (!window.confirm(this.submitConfirmWarning)) {
                e.preventDefault();
                return;
            }
        }

        if (this.checkDoubleSubmit) {
            if (this.isSubmitted) {
                // Previously submitted - don't submit again
                e.preventDefault();
            } else {
                // Mark it so that the next submit can be ignored
                this.isSubmitted = true;
                this.showSubmitted();
            }
        }
    }

    private showSubmitted() {
        this.submitButton.setAttribute('disabled', '');
        if (this.submitMessage) {
            if (this.submitButton.nodeName.toLowerCase() === 'button') {
                this.submitButton.innerHTML = this.submitMessage;
            }
            else {
                this.submitButton.value = this.submitMessage;
            }
        }
    }

    private addUnobtrusiveField(field: FieldElement) {
        if (!FormHelper.isFieldElement(field)) {
            return;
        }
        const self = this;

        const validElement: IValidationElement = {
            field: field,
        };

        field.addEventListener("invalid", function handleInvalidField(event) {
            self.handleInvalid(field, event);
        });

        field.addEventListener("blur", function handleFieldBlur() {
            const validElement = self.validationElements.get(field);
            self.clearErrorMessage(validElement);
            field.checkValidity();
        });

        // is there a related field that gets any messages that we have?
        if (field.name) {
            validElement.messageField = this.form.querySelector('[data-valmsg-for="' + field.name + '"]');
        }

        var attributes = field.getAttributeNames();
        for (const attrkey of attributes) {
            const fixfunc = fixups[attrkey];
            if (fixfunc) {
                fixfunc(validElement, field.getAttribute(attrkey));
            }
        }

        this.validationElements.set(field, validElement);
    }

    private handleInvalid(field: FieldElement, event: Event) {
        // find the validElement
        event.preventDefault(); // suppress browser error
        const validElement = this.validationElements.get(field);
        const validity = field.validity;
        if (!validity) {
            this.setErrorMessage(validElement, null);
            return;
        }

        // check for missing
        if (validity.valueMissing && validElement.requiredMessage) {
            this.setErrorMessage(validElement, validElement.requiredMessage);
            return;
        }
        if (validity.typeMismatch) {
            if (validElement.emailMessage) {
                this.setErrorMessage(validElement, validElement.emailMessage);
                return;
            }
            if (validElement.creditcardMessage) {
                this.setErrorMessage(validElement, validElement.creditcardMessage);
                return;
            }
            if (validElement.phoneMessage) {
                this.setErrorMessage(validElement, validElement.phoneMessage);
                return;
            }
        }
        if (validity.tooLong) {
            this.setErrorMessage(validElement, validElement.maxlengthMessage);
            return;
        }
        if (validity.tooShort) {
            this.setErrorMessage(validElement, validElement.minlengthMessage);
            return;
        }
        if (validity.patternMismatch && validElement.regexMessage) {
            this.setErrorMessage(validElement, validElement.regexMessage);
            return;
        }

        // default...fall throught
        this.setErrorMessage(validElement, null);
    }

    private setErrorMessage(validElement: IValidationElement, message: string) {
        validElement.field.setAttribute("aria-invalid", "true");
        validElement.field.classList.add("input-validation-error");
        if (validElement.messageField) {
            validElement.messageField.innerHTML = message ? message : DEFAULTINVALIDMESSAGE;
            validElement.messageField.classList.remove("field-validation-valid");
            validElement.messageField.classList.add("field-validation-error");
        }
    }

    private clearErrorMessage(validElement: IValidationElement) {
        validElement.field.removeAttribute("aria-invalid");
        validElement.field.classList.remove("input-validation-error");

        if (validElement.messageField) {
            validElement.messageField.innerHTML = '';
            validElement.messageField.classList.add("field-validation-valid");
            validElement.messageField.classList.remove("field-validation-error");
        }
    }

    // Get the validity attribute. This probably isn't necessary, but just to 
    // be safe
    private static isFieldElement(field: Element): boolean {
        if (field instanceof HTMLInputElement ||
            field instanceof HTMLSelectElement ||
            field instanceof HTMLTextAreaElement) {
            return true;
        }
        return false;
    }
}